import React, { useState, useRef, useEffect } from 'react';
import {
  FaUpload,
  FaUniversity,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSearch,
  FaTimes,
  FaFileExcel,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaMoon,
  FaSun
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryApi from '../common/index';
import * as XLSX from 'xlsx';

const UploadStudentExcel = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [collegeOptions, setCollegeOptions] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeDetails, setCollegeDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [recordsCount, setRecordsCount] = useState(0);
  const [headerCheckResults, setHeaderCheckResults] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingColleges, setIsFetchingColleges] = useState(false);
  const [isFetchingCollegeDetails, setIsFetchingCollegeDetails] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    sessionStorage.setItem('admin-theme', newDarkMode ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', newDarkMode);
    
    // Dispatch event for other components
    window.dispatchEvent(new Event('themeChanged'));
    window.dispatchEvent(new Event('adminThemeToggled'));
  };

  useEffect(() => {
    fetchCollegeNames();
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  /* -------------------- API Calls -------------------- */
  const fetchCollegeNames = async () => {
    setIsFetchingColleges(true);
    try {
      const response = await fetch(SummaryApi.fetchcollegenames.url, {
        method: SummaryApi.fetchcollegenames.method,
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.status) {
        setCollegeOptions(data.data);
        setFilteredColleges(data.data);
      } else {
        toast.error(data.message || 'Failed to fetch college names');
      }
    } catch (error) {
      console.error('Error fetching college names:', error);
      toast.error('Error fetching college names');
    } finally {
      setIsFetchingColleges(false);
    }
  };

  const fetchCollegeDetails = async (collegeId) => {
    setIsFetchingCollegeDetails(true);
    try {
      const response = await fetch(SummaryApi.CollegeById.url, {
        method: SummaryApi.CollegeById.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ college_id: collegeId }),
      });
      const data = await response.json();
      if (data.status) {
        setCollegeDetails(data.data);
      } else {
        toast.error(data.message || 'Failed to fetch college details');
      }
    } catch (error) {
      console.error('Error fetching college details:', error);
      toast.error('Error fetching college details');
    } finally {
      setIsFetchingCollegeDetails(false);
    }
  };

  /* -------------------- College Search -------------------- */
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setFilteredColleges(collegeOptions);
    } else {
      const filtered = collegeOptions.filter((college) =>
        college.college_name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredColleges(filtered);
    }
    setShowDropdown(true);
  };

  const handleCollegeSelect = (college) => {
    setSelectedCollege(college);
    setSearchQuery(college.college_name);
    setShowDropdown(false);
    fetchCollegeDetails(college.college_id);
  };

  const clearSelection = () => {
    setSelectedCollege(null);
    setSearchQuery('');
    setCollegeDetails(null);
    setFilteredColleges(collegeOptions);
  };

  /* -------------------- Excel Header Check -------------------- */
  const checkHeaders = (data) => {
    if (!data || data.length < 2) {
      return null;
    }

    const headers = data[0].map((h) => h.toString().toLowerCase().trim());
    const requiredFields = [
      's.no',
      'stu_clg_enrollment_number',
      'first_name',
      'last_name',
      'email',
      'mobile_number',
      'ug_pg',
      'academic_course',
    ];

    const missingFields = requiredFields.filter((f) => !headers.includes(f));

    const dataRows = data.slice(1);
    const nonEmptyRows = dataRows.filter(
      (row) =>
        row.length > 0 &&
        !row.every((cell) => cell === null || cell === undefined || cell === '')
    );

    const result = {
      totalRecords: nonEmptyRows.length,
      headersMatch: missingFields.length === 0,
      missingHeaders: missingFields,
    };

    if (!result.headersMatch) {
      toast.error(`Missing required columns: ${missingFields.join(', ')}`);
    } else {
      // toast.success(
      //   `Headers match! ${nonEmptyRows.length} student record(s) found.`
      // );
    }

    return result;
  };

  /* -------------------- File Handling -------------------- */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const validExt = /\.(xlsx|xls|csv)$/i;

    if (!validTypes.includes(file.type) && !validExt.test(file.name)) {
      toast.error('Please upload a valid Excel/CSV file');
      return;
    }

    setExcelFile(file);
    const reader = new FileReader();

    reader.onload = (ev) => {
      try {
        const binary = ev.target.result;
        const workbook = file.type.includes('csv')
          ? XLSX.read(binary, { type: 'string' })
          : XLSX.read(binary, { type: 'binary' });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const rows = rawData.slice(1);
        const count = rows.filter(
          (r) =>
            r.length > 0 &&
            !r.every((c) => c === null || c === undefined || c === '')
        ).length;

        setRecordsCount(count);
        setExcelData(rawData);

        const check = checkHeaders(rawData);
        setHeaderCheckResults(check);

        if (count > 1000) {
          toast.warn('File exceeds 1000 records. Split into smaller files.');
        } else if (count > 0) {
          toast.success(`Loaded ${count} record(s) from the file.`);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to read the file');
      }
    };

    if (file.type.includes('csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const removeFile = () => {
    setExcelFile(null);
    setExcelData(null);
    setRecordsCount(0);
    setHeaderCheckResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setIsUploaded(false);
    removeFile();
    setSelectedCollege(null);
    setSearchQuery('');
    setCollegeDetails(null);
    setFilteredColleges(collegeOptions);
  };

  /* -------------------- Duplicate Window -------------------- */
  const showDuplicateRecords = (dup) => {
    const win = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
 <title>Existing Records</title>

        <style>
          body{font-family:Arial,sans-serif;margin:20px;background:#f8fafc;color:#1e293b;}
          .dark body{background:#0f172a;color:#f1f5f9;}
          h1{color:#e53e3e;}
          .dark h1{color:#fecaca;}
          .header-container{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;flex-wrap:wrap;gap:10px;}
          .download-btn{background:#3182ce;color:#fff;padding:10px 15px;border:none;border-radius:4px;cursor:pointer;display:flex;align-items:center;gap:8px;}
          .download-btn:hover{background:#2c5282;}
          .dark .download-btn{background:#60a5fa;}
          .dark .download-btn:hover{background:#3b82f6;}
          table{border-collapse:collapse;width:100%;margin:20px 0;}
          th,td{border:1px solid #ddd;padding:8px;text-align:left;}
          th{background:#f2f2f2;position:sticky;top:0;}
          .dark th{background:#334155;color:#f1f5f9;}
          .dark td{background:#1e293b;color:#f1f5f9;border-color:#475569;}
          tr:nth-child(even){background:#f9f9f9;}
          .dark tr:nth-child(even){background:#334155;}
          .pagination{display:flex;justify-content:center;margin:20px 0;gap:5px;}
          .pagination button{padding:8px 12px;border:1px solid #ddd;background:#fff;cursor:pointer;border-radius:4px;}
          .pagination button:hover:not(:disabled){background:#f0f0f0;}
          .pagination button:disabled{cursor:not-allowed;opacity:.5;}
          .dark .pagination button{background:#334155;color:#f1f5f9;border-color:#475569;}
          .dark .pagination button:hover:not(:disabled){background:#475569;}
          .page-info{padding:8px 12px;display:flex;align-items:center;}
          .footer{margin-top:20px;text-align:center;font-weight:bold;color:#555;}
          .dark .footer{color:#94a3b8;}
          @media(max-width:768px){
            .header-container{flex-direction:column;align-items:flex-start;}
            table{font-size:12px;}
            th,td{padding:6px;}
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <h1>Existing Records (${dup.count})</h1>
          <button class="download-btn" onclick="downloadExcel()">Download Excel</button>
        </div>
        <p>The following records were skipped due to Existing:</p>
        <div id="table-container">
          <table id="tbl"><thead><tr>
            <th>S.No</th><th>First Name</th><th>Last Name</th><th>Email</th><th>Mobile</th>
          </tr></thead><tbody id="tbody"></tbody></table>
        </div>
        <div class="pagination" id="pag"></div>
        <div class="footer">Total: ${dup.count}</div>

        <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
        <script>
          const data = ${JSON.stringify(dup.data)};
          const perPage = 10;
          let page = 1;

          const render = () => {
            const tbody = document.getElementById('tbody');
            const start = (page-1)*perPage;
            const end = start+perPage;
            const slice = data.slice(start,end);
            tbody.innerHTML = '';
            slice.forEach((r,i)=>{
              const tr = document.createElement('tr');
              tr.innerHTML = \`<td>\${start+i+1}</td>
                <td>\${r.first_name||'N/A'}</td>
                <td>\${r.last_name||'N/A'}</td>
                <td>\${r.email||'N/A'}</td>
                <td>\${r.mobile_number||'N/A'}</td>\`;
              tbody.appendChild(tr);
            });
            paginate();
          };

          const paginate = () => {
            const total = Math.ceil(data.length/perPage);
            const cont = document.getElementById('pag');
            cont.innerHTML = \`
              <button onclick="go(1)" \${page===1?'disabled':''}>First</button>
              <button onclick="go(page-1)" \${page===1?'disabled':''}>Prev</button>
              <span class="page-info">Page \${page} of \${total}</span>
              <button onclick="go(page+1)" \${page===total?'disabled':''}>Next</button>
              <button onclick="go(total)" \${page===total?'disabled':''}>Last</button>
            \`;
          };

          const go = (p) => { if(p>=1 && p<=Math.ceil(data.length/perPage)){ page=p; render(); } };

          const downloadExcel = () => {
            const ws = XLSX.utils.json_to_sheet(data.map((r,i)=>({
              'S.No':i+1,
              'First Name':r.first_name||'N/A',
              'Last Name':r.last_name||'N/A',
              Email:r.email||'N/A',
              'Mobile Number':r.mobile_number||'N/A'
            })));
            ws['!cols'] = [{wch:8},{wch:15},{wch:15},{wch:25},{wch:15}];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb,ws,'Duplicates');
            const d = new Date();
            const ts = \`\${d.getFullYear()}\${String(d.getMonth()+1).padStart(2,'0')}\${String(d.getDate()).padStart(2,'0')}_\${String(d.getHours()).padStart(2,'0')}\${String(d.getMinutes()).padStart(2,'0')}\`;
            XLSX.writeFile(wb,\`duplicate_records_\${ts}.xlsx\`);
          };

          render();
        </script>
      </body>
      </html>`;
    win.document.write(html);
    win.document.close();
  };

  /* -------------------- Upload Logic -------------------- */
  const uploadExcel = async () => {
    if (!selectedCollege) {
      toast.error('Select a college first');
      return;
    }
    if (!excelFile) {
      toast.error('Select an Excel file');
      return;
    }
    if (recordsCount > 1000) {
      toast.error('File exceeds 1000-record limit');
      return;
    }
    if (!headerCheckResults?.headersMatch) {
      toast.error('Headers do not match');
      return;
    }

    setIsUploading(true);
    try {
      const form = new FormData();
      form.append('excel_file', excelFile);
      form.append('college_id', selectedCollege.college_id);

      const res = await fetch(SummaryApi.uploadexcel.url, {
        method: SummaryApi.uploadexcel.method,
        body: form,
      });
      const json = await res.json();

      if (json.status) {
        const dupCount = json.duplicates?.count ?? 0;

        // Show success toast FIRST
        toast.success('Excel uploaded successfully!');
        
        // Wait a bit before resetting to allow toasts to be visible
        setTimeout(() => {
          setIsUploaded(true);
          resetForm();
          
          // Show duplicates in a new window after a short delay
          if (dupCount > 0) {
            setTimeout(() => {
              showDuplicateRecords(json.duplicates);
            }, 500);
          }
        }, 1000); // 1 second delay to ensure toast is visible
        
      } else {
        toast.error(json.message || 'Upload failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Upload error');
    } finally {
      setIsUploading(false);
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <div className={`min-h-screen py-8 px-4 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className={`max-w-5xl mx-auto rounded-2xl shadow-lg p-6 md:p-10 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white'
      }`}>
        {/* Dark Mode Toggle */}
        <div className="flex justify-between items-center mb-6">
          <h1 className={`text-2xl md:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Upload Student Excel
          </h1>

        </div>

        {/* Success Banner */}
        {isUploaded && (
          <div className="mt-4 flex flex-col items-center">
            <div className={`p-3 rounded-md mb-2 transition-colors duration-300 ${
              isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'
            }`}>
              Excel file uploaded successfully.
            </div>
            <button
              onClick={resetForm}
              className={`px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                isDarkMode 
                  ? 'bg-red-700 hover:bg-red-600 shadow-lg shadow-red-700/30' 
                  : 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/30'
              }`}
            >
              Reset to Upload Another File
            </button>
          </div>
        )}

        {/* College Selector */}
        <div className="mb-8">
          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Select College <span className="text-red-500">*</span>
          </label>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <FaUniversity className={`absolute left-3 top-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search for a college..."
                className={`block w-full h-11 pl-10 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                }`}
                disabled={isFetchingColleges || isUploaded}
              />
              {searchQuery && (
                <button
                  onClick={clearSelection}
                  className={`absolute right-3 top-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {showDropdown && filteredColleges.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-y-auto transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}>
                {filteredColleges.map((c) => (
                  <div
                    key={c.college_id}
                    onClick={() => handleCollegeSelect(c)}
                    className={`h-12 px-4 flex flex-col justify-center cursor-pointer border-b last:border-b-0 transition-colors duration-300 ${
                      isDarkMode 
                        ? 'hover:bg-blue-900/30 border-gray-600' 
                        : 'hover:bg-blue-50 border-gray-100'
                    }`}
                  >
                    <div className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-800'
                    }`}>{c.college_name}</div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>{c.college_location}</div>
                  </div>
                ))}
              </div>
            )}

            {isFetchingColleges && (
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Loading colleges...</p>
            )}
          </div>

          {collegeDetails && (
            <div className={`mt-4 p-4 rounded-md border transition-colors duration-300 ${
              isDarkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'
            }`}>
              <h3 className={`font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-blue-300' : 'text-blue-800'
              }`}>
                Selected College Details:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span className="font-medium">Name:</span> {collegeDetails.college_name}
                </div>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span className="font-medium">Code:</span> {collegeDetails.college_code}
                </div>
                <div className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span className="font-medium">Address:</span> {collegeDetails.address}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* File Drop Zone */}
        <div className={`border-2 border-dashed rounded-xl p-8 text-center mb-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'border-gray-600 bg-gray-700/50 hover:bg-gray-700' 
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        }`}>
          {excelFile ? (
            <div className="flex flex-col items-center">
              <div className={`rounded-full p-3 mb-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-green-900/30' : 'bg-green-100'
              }`}>
                <FaCheckCircle className={`text-2xl transition-colors duration-300 ${
                  isDarkMode ? 'text-green-400' : 'text-green-600'
                }`} />
              </div>
              <h3 className={`text-lg font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                {excelFile.name}
              </h3>
              <p className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {recordsCount} record(s) • {(excelFile.size / 1024).toFixed(2)} KB
              </p>

              {recordsCount > 1000 && (
                <div className={`flex items-center px-4 py-2 rounded-md mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-50 text-yellow-700'
                }`}>
                  <FaExclamationTriangle className="mr-2" />
                  <span>File exceeds 1000-record limit</span>
                </div>
              )}

              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={removeFile}
                  className={`border rounded-md font-medium text-sm h-10 w-32 flex items-center justify-center transition-colors duration-300 ${
                    isDarkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-600 bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100 bg-white'
                  }`}
                >
                  Change File
                </button>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer w-full">
              <div className="flex flex-col items-center justify-center">
                <div className={`rounded-full w-16 h-16 flex items-center justify-center mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'
                }`}>
                  <FaUpload className={`text-2xl transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <h3 className={`text-lg font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  Upload Excel File
                </h3>
                <p className={`text-sm mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Supports .xlsx, .xls, .csv (max 1000 records)
                </p>
                <span className={`h-10 w-36 flex items-center justify-center text-white rounded-md cursor-pointer transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-blue-700 hover:bg-blue-600 shadow-lg shadow-blue-700/30' 
                    : 'bg-blue-900 hover:bg-blue-700 shadow-lg shadow-blue-600/30'
                }`}>
                  Browse Files
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                disabled={isUploaded}
              />
            </label>
          )}
        </div>

        {/* Header Check Summary */}
        {headerCheckResults && (
          <div className={`mb-6 p-4 border rounded-lg transition-colors duration-300 ${
            isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
          }`}>
            <h3 className={`text-lg font-medium mb-3 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>File Check</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className={`p-3 rounded-md shadow text-center transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700' : 'bg-white'
              }`}>
                <div className={`text-2xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {headerCheckResults.totalRecords}
                </div>
                <div className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Total Records</div>
              </div>
              <div
                className={`p-3 rounded-md shadow text-center transition-colors duration-300 ${
                  headerCheckResults.headersMatch
                    ? isDarkMode ? 'bg-green-900/30' : 'bg-green-50'
                    : isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
                }`}
              >
                <div className={`text-2xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {headerCheckResults.headersMatch ? 'Check' : 'Cross'}
                </div>
                <div className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {headerCheckResults.headersMatch
                    ? 'Headers Match'
                    : "Headers Don't Match"}
                </div>
              </div>
            </div>

            {headerCheckResults.missingHeaders.length > 0 && (
              <div>
                <h4 className={`font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Missing Columns:
                </h4>
                <ul className={`text-sm space-y-1 max-h-40 overflow-y-auto transition-colors duration-300 ${
                  isDarkMode ? 'text-red-400' : 'text-red-600'
                }`}>
                  {headerCheckResults.missingHeaders.map((h, i) => (
                    <li key={i} className={`p-1 rounded transition-colors duration-300 ${
                      isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
                    }`}>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Upload Button */}
        <div className="text-center">
          <button
            onClick={uploadExcel}
            disabled={
              !excelFile ||
              !selectedCollege ||
              recordsCount > 1000 ||
              isUploading ||
              (headerCheckResults && !headerCheckResults.headersMatch) ||
              isUploaded
            }
            className={`px-6 py-3 text-white rounded-lg shadow-md flex items-center justify-center mx-auto min-w-[160px] transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-blue-700 hover:bg-blue-600 shadow-blue-700/30' 
                : 'bg-blue-900 hover:bg-blue-700 shadow-blue-600/30'
            } ${
              !excelFile || !selectedCollege || recordsCount > 1000 || isUploading || 
              (headerCheckResults && !headerCheckResults.headersMatch) || isUploaded
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              'Upload Students'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadStudentExcel;